import { useState } from 'react';

import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { videos, downloadableDocs } from './constants';
import { EmailContent } from './email-popup';
import { getStaticAssetPresignedUrl } from './helpers';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import Modal from '@/components/molecules/Modals/CustomModal';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';

export function ClientMaterials() {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const { fetchConfigGET } = useGetFetchConfig();

  const handleDownloadStaticAsset = async (key: string) => {
    try {
      trackCustomEvent(TrackingEvents.DownloadOverviewDocument);
      const url = await getStaticAssetPresignedUrl(key, fetchConfigGET);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Downloading ${key}`);
      return new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleCopyVideoUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Copied to clipboard', {
      description: url,
      duration: 1000,
    });
  };

  const openEmailModal = () => {
    setIsEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    setIsEmailModalOpen(false);
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/client-materials', label: 'Client Materials', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Client Materials" />
        </PageHeader>

        {/* Trust & Security Section */}
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-3">
            <Icon className="lucide--lock text-brandSecondary h-4 w-4" />

            <h2 className="font-serif text-xl font-medium">Trust & Security</h2>
          </div>
          <p className="mb-2 text-gray-600">
            Our trust site provides detailed information about our security practices, data handling policies, and
            compliance certifications.
          </p>
          <Link
            to="https://trust.wexler.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs font-medium text-blue-500 hover:text-blue-600"
          >
            Visit our Trust Center
            <Icon className="lucide--external-link h-4 w-4" />
          </Link>
        </div>

        {/* Overview Documents Section */}
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon className="lucide--file-text text-brandSecondary h-4 w-4" />
              <h2 className="font-serif text-xl font-medium">Overview Documents</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={openEmailModal}>
                <Icon className="lucide--mail h-4 w-4" />
                <span>Email Template</span>
              </Button>
            </div>
          </div>
          <p className="mb-6 text-gray-500">
            These documents provide detailed information about our services and address common questions from clients.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {downloadableDocs.map((material, index) => (
              <Button key={index} onClick={() => handleDownloadStaticAsset(material.url)}>
                {material.title}
                <Icon className="lucide--download h-4 w-4" />
              </Button>
            ))}
          </div>
        </div>

        {/* Feature Videos Section */}
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-3">
            <Icon className="lucide--video text-brandSecondary h-4 w-4" />
            <h2 className="font-serif text-xl font-medium">Feature Videos</h2>
          </div>
          <p className="mb-4 text-gray-500">Videos explaining Wexler's main features.</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {videos.map((video, index) => (
              <div key={index} className="flex h-full flex-col justify-between rounded-md border bg-gray-50/50 p-4">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    {video.icon}
                    <h4 className="font-medium text-gray-800">{video.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{video.description}</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <a
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Icon className="lucide--external-link mr-1 h-3.5 w-3.5" />
                    Watch Video
                  </a>
                  <button
                    onClick={() => handleCopyVideoUrl(video.url)}
                    className="flex flex-1 items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    <Icon className="lucide--copy mr-1 h-3.5 w-3.5" />
                    Copy Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Modal isOpen={isEmailModalOpen} handleClose={closeEmailModal} content={<EmailContent />} maxWidth="40rem" />
      </PageContainer>
    </>
  );
}
